import { Button, ButtonType } from '@soteria/design-system-ve';
import React from 'react';

import * as styles from './SectionHeader.css';

export interface SectionHeaderActionConfig {
  text: string;
  onClick: () => void;
  buttonType?: ButtonType;
  disabled?: boolean;
}

export interface SectionHeaderProps {
  /**
   * The main title text for the section header
   */
  title: string;

  /**
   * Optional description text that appears below the title
   */
  description?: string;

  /**
   * Optional action button configuration for the right side
   */
  action?: SectionHeaderActionConfig;

  /**
   * Optional component to render on the left side (start)
   * When provided, this takes precedence over title/description
   */
  startComponent?: React.ReactNode;

  /**
   * Optional component to render in the center
   * When provided along with startComponent, the title/description are ignored
   */
  centerComponent?: React.ReactNode;

  /**
   * Optional component to render on the right side (end)
   * When provided, this takes precedence over the action button
   */
  endComponent?: React.ReactNode;

  /**
   * Additional CSS class name to apply to the root element
   */
  className?: string;
}

/**
 * Flexible section header component that supports both simple title/description layout
 * and advanced three-component layout (start, center, end)
 *
 * Simple usage:
 * <SectionHeader title="My Section" description="Section description" action={{...}} />
 *
 * Advanced usage:
 * <SectionHeader
 *   startComponent={<CustomTitle />}
 *   centerComponent={<CustomCenter />}
 *   endComponent={<CustomActions />}
 * />
 */
export function SectionHeader({ title, description, action, startComponent, centerComponent, endComponent, className }: SectionHeaderProps) {
  // Determine if we're using the three-component layout
  const isTriComponentLayout = startComponent || centerComponent || endComponent;

  // Render start section
  const renderStartSection = () => {
    if (startComponent) {
      return <div className={styles.startSection}>{startComponent}</div>;
    }

    // Default to title/description if no startComponent provided
    return (
      <div className={styles.startSection}>
        <div className={styles.content}>
          <h2 className={styles.title}>{title}</h2>
          {description && <p className={styles.description}>{description}</p>}
        </div>
      </div>
    );
  };

  // Render center section
  const renderCenterSection = () => {
    if (!centerComponent) {
      return null;
    }

    return <div className={styles.centerSection}>{centerComponent}</div>;
  };

  // Render end section
  const renderEndSection = () => {
    if (endComponent) {
      return <div className={styles.endSection}>{endComponent}</div>;
    }

    // Default to action button if no endComponent provided
    if (action) {
      return (
        <div className={styles.endSection}>
          <Button buttonType={action.buttonType || ButtonType.Primary} disabled={action.disabled} onClick={action.onClick}>
            {action.text}
          </Button>
        </div>
      );
    }

    return null;
  };

  const rootClassName = isTriComponentLayout ? styles.triComponentHeader : styles.sectionHeader;

  return (
    <div className={`${rootClassName} ${className || ''}`}>
      {renderStartSection()}
      {renderCenterSection()}
      {renderEndSection()}
    </div>
  );
}
